<script setup lang="ts">
import type { ComboSuggestion } from "@innoclouds/api";
import type { ProductEntry } from "@innoclouds/db";
import { sizeStates } from "@innoclouds/ui";
import { CARD_PRICE_DEFAULT, type CardPriceControl, cardPriceVariants } from "@innoclouds/ui/templates/main/cardPrice/cardPrice.ts";
import { useTranslation } from "i18next-vue";
import { motion } from "motion-v";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import Heart from "#/assets/iconsDynamic/Heart.vue";
import ProductBadges from "#/components/productBadges/ProductBadges.vue";
import { addProductWithCombo } from "#/composables/useAddProductWithCombo";
import { useNutritionBasis } from "#/composables/useNutritionBasis";
import { usePdf } from "#/composables/usePdfMode";
import { useProductLike } from "#/composables/useProductLike";
import AnimationCard from "#/hooks/useAnimationLoader/AnimationLoader.vue";
import { useCardStore } from "#/store/card";
import { useDecorationStore } from "#/store/decoration";
import { useMainStore } from "#/store/main";
import { useScheduleStore } from "#/store/schedule";
import { useThemeStore } from "#/store/theme";
import type { IProduct } from "#/types/iCardProduct";
import type { IGroup } from "#/types/iMain";
import Loader from "#/ui/loader/Loader.vue";
import NutritionBasis from "#/ui/nutritionBasis/NutritionBasis.vue";
import { formatPrice, getLocaleFor, getLocaleName } from "#/utils/i18next";
import {
	calculateProductNutritions,
	emptyNutritions,
	formatNutritionWeight,
	hasNutritionValue,
	resolveBaseNutritions,
	toCartNutritionFields,
} from "#/utils/nutritions";
import PricePrerender from "#/utils/pricePrerender";

const { t, i18next } = useTranslation();
const storeMain = useMainStore();
const cardStoreLib = useCardStore();
const themeStore = useThemeStore();
const schedule = useScheduleStore();

const { is_18_above_content, is18Allowed, notificationData_lib } = storeToRefs(storeMain);

const { isDesktop } = usePdf();

const props = defineProps<{
	data: ProductEntry | IProduct | IGroup | ComboSuggestion;
	activeTags: string[];
	is_group: boolean;
	is_combo?: boolean;
	parent: string;
	isLoading: boolean;
	disableComboSearch?: boolean;
	hideAddButton?: boolean;
	hideLike?: boolean;
	rank?: number;
	compact?: boolean;
	variant?: "catalog" | "combo";
	qr_menu?: boolean;
	priceOverride?: number | null;
	comboSurcharge?: number | null;
	selectGroupChild?: (item: any) => void;
	takeCurrentCard?: (id: ProductEntry["id"]) => void | Promise<void>;
	onAddToCart?: (item: IProduct) => void;
	decoration?: string;
	size?: string | null;
}>();

const decor = useDecorationStore().screen(props.decoration);

const cardAddButtonLayoutId = `card_add_button_wrapper_${crypto.randomUUID()}`;

const isComboVariant = computed(() => props.variant === "combo");
const sized = computed(() => sizeStates(props.size));
const bodyBinding = computed(() => (isComboVariant.value ? decor.bind("combo_card_body") : decor.bind("card_body", sized.value)));
const description = computed(() => getLocaleFor("description", props.data, i18next.language) || null);
const baseNutritions = computed(() => (props.is_group ? emptyNutritions() : resolveBaseNutritions(props.data as IProduct)));
const { hasBasisToggle, nutritions } = useNutritionBasis(baseNutritions, () => decor.visible("nutrition_basis", false));
const macroLabel = (key: string, value: number) => (hasNutritionValue(value) ? t(key, { amount: formatPrice(value) }) : null);
const nutritionParts = computed(() => ({
	weight: decor.visible("card_weight", false)
		? formatNutritionWeight(nutritions.value, (value, unit) =>
				formatPrice(value, unit === "gram" ? t(`card.nutritional.unit.gram`) : t(`card.nutritional.unit.milliliter`)),
			) || null
		: null,
	calories: decor.visible("card_calories", false) ? macroLabel("modal.product-description.energy-amount", nutritions.value.energy_amount) : null,
	proteins: decor.visible("card_proteins", false) ? macroLabel("modal.product-description.proteins-amount", nutritions.value.proteins_amount) : null,
	fats: decor.visible("card_fats", false) ? macroLabel("modal.product-description.fat-amount", nutritions.value.fat_amount) : null,
	carbs: decor.visible("card_carbs", false) ? macroLabel("modal.product-description.carbohydrates-amount", nutritions.value.carbohydrates_amount) : null,
}));
const hasNutritionParts = computed(() => Object.values(nutritionParts.value).some(Boolean));
const hasMacroParts = computed(() =>
	[nutritionParts.value.calories, nutritionParts.value.proteins, nutritionParts.value.fats, nutritionParts.value.carbs].some(Boolean),
);
const showCombo = computed(() => props.qr_menu && !props.disableComboSearch);
const minPrice = computed(() => {
	if (!themeStore.data?.product_price_prefix || props.is_group || isComboVariant.value || props.priceOverride || props.priceOverride === 0) return null;
	return (props.data as IProduct).min_price;
});
const priceContent = computed(() => cardPriceVariants[decor.variant("card_price", CARD_PRICE_DEFAULT)] ?? cardPriceVariants[CARD_PRICE_DEFAULT]);
const priceControl = computed(
	(): CardPriceControl => ({
		prefix: t("card.price.from"),
		price: formatPrice(minPrice.value ?? 0, storeMain.config_lib.currency_symbol),
		prefixAttrs: decor.bind("card_price_prefix"),
		valueAttrs: decor.bind("card_price_value"),
	}),
);

const addToCart = (item: IProduct, data: any) => {
	if (schedule.isClosed) return schedule.notify();

	const nutritions = toCartNutritionFields(calculateProductNutritions({ product: item, amount: 1 }));

	const fastItem = {
		id: item.id,
		is_fake: item.is_fake || false,
		is_od_united_product: item.is_od_united_product || false,
		adult: item.adult,
		is_marked: !!item.is_marked,
		amount: item.amount || 1,
		name: item.name,
		loc_name: item.loc_name,
		image_link: item.image_link,
		price: item.price,
		old_price: item.price,
		size: undefined,
		sizes: [],
		portion_nutrition: item.portion_nutrition ?? null,
		modifiers: [],
		groups: item.groups,
		group_modifiers: [],
		...nutritions,
	};

	const canFastAdd = !item.modifiers?.length && !item.group_modifiers?.length && !item.sizes?.length && (item as any).has_combo !== true && !props.is_combo;
	cardStoreLib.isUpSale = false;

	if (item.adult && !is18Allowed.value) {
		is_18_above_content.value = true;
		if (canFastAdd) notificationData_lib.value.fastAdd = fastItem;
		return;
	}

	if (!item.amount) {
		item.amount = 1;
	}
	if (props.is_group && props.selectGroupChild) {
		props.selectGroupChild({ ...item, parent: props.parent, child: data.child_groups });
		return;
	}
	if (props.onAddToCart) {
		props.onAddToCart(data);
		return;
	}
	if (canFastAdd) {
		storeMain.addToCart(fastItem);
		return;
	}
	if (props.takeCurrentCard) {
		props.takeCurrentCard(data.id);
	}
};

const addToCartWithCombo = async (item: IProduct, originalData: any) => {
	if (props.disableComboSearch) {
		addToCart(item, originalData);
		return;
	}

	const hasCombo = (item as any).has_combo === true;

	if (showCombo.value) {
		addToCart(item, originalData);
		return;
	} else {
		const hasCombo = (item as any).has_combo === true;

		if (!hasCombo) {
			addToCart(item, originalData);
			return;
		}
	}

	if (item.adult && !is18Allowed.value) {
		is_18_above_content.value = true;
		notificationData_lib.value.callback = () => addToCartWithCombo(item, originalData);
		return;
	}

	if ((item as any)._comboProcessing) return;
	(item as any)._comboProcessing = true;

	try {
		await addProductWithCombo(item, {
			onFastAdd: () => addToCart(item, originalData),
			onOpenCard: props.takeCurrentCard
				? async (productId?: ProductEntry["id"]) => {
						if (!productId) return;
						cardStoreLib.setCurrentCard(item);
						await Promise.resolve(props.takeCurrentCard?.(productId));
					}
				: undefined,
		});
	} catch (err) {
		console.error("addProductWithCombo failed:", err);
		addToCart(item, originalData);
	} finally {
		setTimeout(() => {
			(item as any)._comboProcessing = false;
		}, 300);
	}
};

const handleTakeCurrentCard = (item: any) => {
	if (props.is_group && props.selectGroupChild) {
		item = { ...item, parent: props.parent, child: item.child_groups };
		props.selectGroupChild(item);
		return;
	}
	if (!isComboVariant.value && item.adult && !is18Allowed.value) {
		is_18_above_content.value = true;
		return;
	}

	if (props.takeCurrentCard) {
		props.takeCurrentCard(item.id);
	}
};

const tags = computed(() => getLocaleFor("tags", props.data, i18next.language) || []);

const badgeKeys = computed(() => (props.is_group ? [] : ((props.data as IProduct).badges ?? [])));
const badgesOnPhoto = computed(() => themeStore.data?.badge_placement !== "under_title");
const badgesLimit = computed(() => themeStore.data?.max_badges_on_photo ?? 3);

const activateTags = computed(() => props.activeTags.every((tag: string) => tags.value.includes(tag)));
const cartItems = computed(() => {
	if (props.is_group) return [];
	return storeMain.cartProducts_lib.filter((item) => item.id === props.data.id);
});

const cartCount = computed(() => cartItems.value.reduce((acc, item) => acc + item.amount, 0));

const decreaseAmount = () => {
	if (cartCount.value === 0) return;
	const lastItem = cartItems.value[cartItems.value.length - 1];
	if (lastItem.amount === 1) {
		storeMain.deleteItemFromCart(lastItem.idSystem);
	} else {
		storeMain.minusAmount(lastItem.idSystem);
	}
};

const increaseAmount = () => {
	addToCartWithCombo(props.data as IProduct, props.data);
};

const imageSrc = computed(() => props.data.image_link || themeStore.data?.fallback_image || "");

const productId = computed(() => (props.is_group ? null : props.data.id));
const { isLikesEnabled, isLiked, isLikePending, likeCount, toggleLike } = useProductLike(productId);
const showLikes = computed(() => isLikesEnabled.value && !props.is_group && !props.is_combo && !props.hideLike && !isComboVariant.value);

const comboPriceText = computed(() => {
	const item = props.data as any;
	const modRaw = props.comboSurcharge ?? item.price_modification;
	if (modRaw !== undefined && modRaw !== null) {
		const modPrice = Number(modRaw);
		if (modPrice === 0) return null;
		const formatted = formatPrice(modPrice, storeMain.config_lib.currency_symbol);
		return modPrice > 0 ? `+ ${formatted}` : formatted;
	}
	return PricePrerender(item);
});
</script>

<template>
	<div v-if="props.activeTags?.length === 0 || activateTags || props.is_group">
		<AnimationCard :id="props.data.id">
			<template #opacityLoader>
				<div class="card__main__wrapper" :class="{ combo: isComboVariant }" v-bind="bodyBinding">
					<div v-if="is_group" class="folder_content_top" />
					<div @click="handleTakeCurrentCard(props.data)" class="card__main" :class="{ card__main_folder: is_group }" v-bind="decor.bind('card_inner', sized)">
						<div v-if="isLoading" class="loader_card_main">
							<Loader />
						</div>
						<div v-else-if="compact" class="card__main_content_compact">
							<div class="image_wrapper_compact" :class="{ 'adult': (props.data as IProduct).adult && !is18Allowed }">
								<img :src="imageSrc" loading="lazy" crossorigin="anonymous" :alt="data.name">
							</div>
							<button
								v-if="showLikes"
								type="button"
								class="card_like_wrapper"
								:data-liked="isLiked"
								:data-pending="isLikePending"
								:disabled="isLikePending"
								@click.stop.prevent="toggleLike"
							>
								<Heart :filled="isLiked" />
								<span v-if="likeCount > 0" class="card_like_count">{{ likeCount }}</span>
							</button>
							<p class="title_compact">{{ getLocaleName(data, i18next.language) }}</p>
							<button
								v-if="!is_group && !isComboVariant"
								type="button"
								class="add_button_compact"
								@click.stop.prevent="addToCartWithCombo(data as IProduct, props.data)"
							>
								+ {{ formatPrice(props.priceOverride ?? PricePrerender(data), storeMain.config_lib.currency_symbol) }}
							</button>
						</div>
						<div v-else class="card__main_content" :class="{card_main_desktop_qr: isDesktop}" v-bind="decor.bind('card_content', sized)">
							<div class="image_wrapper" :class="{ 'adult': (props.data as IProduct).adult && !is18Allowed }" v-bind="decor.bind('card_photo', sized)">
								<img :src="imageSrc" loading="lazy" crossorigin="anonymous" :alt="data.name" v-bind="decor.bind('card_image', sized)">
								<div v-if="props.rank" class="rank_badge" v-bind="decor.bind('card_rank')">#{{ props.rank }}</div>
								<ProductBadges v-if="badgesOnPhoto" :badges="badgeKeys" placement="photo" :limit="badgesLimit" v-bind="decor.bind('card_badges')" />
								<button
									v-if="showLikes && decor.visible('card_like')"
									type="button"
									class="card_like_wrapper"
									:data-liked="isLiked"
									:data-pending="isLikePending"
									:disabled="isLikePending"
									v-bind="decor.bind('card_like')"
									@click.stop.prevent="toggleLike"
								>
									<Heart :filled="isLiked" />
									<span v-if="likeCount > 0" class="card_like_count">{{ likeCount }}</span>
								</button>
								<div v-if="!is_group && !isDesktop && !isComboVariant && !hideAddButton" class="card_button_add_wrapper" v-bind="decor.bind('card_add')">
									<motion.div
										v-if="cartCount > 0 && decor.visible('card_counter')"
										:layoutId="cardAddButtonLayoutId"
										:transition="{ duration: 0.1 }"
										class="cart_count_wrapper"
										v-bind="decor.bind('card_counter')"
										@click.stop.prevent
									>
										<button type="button" class="cart_count_btn" @click.stop.prevent="decreaseAmount">−</button>
										<span class="cart_count">{{ cartCount }}</span>
										<button type="button" class="cart_count_btn" @click.stop.prevent="increaseAmount">+</button>
									</motion.div>
									<motion.div
										v-else
										:layoutId="cardAddButtonLayoutId"
										:transition="{ duration: 0.07 }"
										@click.stop.prevent="addToCartWithCombo(data as IProduct, props.data)"
										class="add_button"
										v-bind="decor.bind('card_add_button')"
									>
										<div class="image_add_button" v-bind="decor.bind('card_add_icon')" />
									</motion.div>
								</div>
							</div>
							<div class="card_title_price_wrapper" v-bind="decor.bind('card_info', sized)">
								<p class="title" v-bind="decor.bind('card_title', sized)">{{ getLocaleName(data, i18next.language) }}</p>
								<div v-if="hasNutritionParts" class="nutrition" v-bind="decor.bind('card_nutrition', sized)">
									<NutritionBasis v-if="hasBasisToggle && hasMacroParts" :decoration="decoration" />
									<span v-if="nutritionParts.weight" v-bind="decor.bind('card_weight', sized)">{{ nutritionParts.weight }}</span>
									<span v-if="nutritionParts.calories" v-bind="decor.bind('card_calories', sized)">{{ nutritionParts.calories }}</span>
									<span v-if="nutritionParts.proteins" v-bind="decor.bind('card_proteins', sized)">{{ nutritionParts.proteins }}</span>
									<span v-if="nutritionParts.fats" v-bind="decor.bind('card_fats', sized)">{{ nutritionParts.fats }}</span>
									<span v-if="nutritionParts.carbs" v-bind="decor.bind('card_carbs', sized)">{{ nutritionParts.carbs }}</span>
								</div>
								<p v-if="description" class="description" v-bind="decor.bind('card_description', sized)">{{ description }}</p>
								<ProductBadges v-if="!badgesOnPhoto" :badges="badgeKeys" placement="inline" size="sm" :limit="badgesLimit" v-bind="decor.bind('card_badges')" />
								<p class="price" v-if="minPrice || minPrice === 0" v-bind="decor.bind('card_price', sized)">
									<component :is="priceContent" :control="priceControl" />
								</p>
								<p class="price" v-else-if="isComboVariant ? comboPriceText !== null : !is_group" v-bind="decor.bind('card_price', sized)">
									{{ isComboVariant ? comboPriceText : formatPrice(props.priceOverride ?? PricePrerender(data), storeMain.config_lib.currency_symbol) }}
								</p>
							</div>
						</div>
					</div>
				</div>
			</template>
		</AnimationCard>
	</div>
</template>

<style scoped lang="scss">
  @use './cardMain.scss';
</style>
